import React from "react";
import dayjs from "dayjs";
import { Box, Stack, Typography } from "@mui/material";

import { DataRateChange, HubIcon } from "../../components";
import { StyledBarTooltip } from "../../containers/DailyThroughput/styles";

const currency = (v) => {
  if (v == null || isNaN(v)) return "$0";
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  return `$${Math.round(v)}`;
};

const getRate = (curr, prev) => {
  if (!prev) return undefined;
  return Math.round(((curr - prev) / prev) * 10000) / 100;
};

const MetricRow = ({ color, label, value, rate, sinceLabel }) => (
  <Stack direction="row" gap="10px" alignItems="center">
    <Box
      sx={{
        width: "4px",
        height: "44px",
        borderRadius: "3px",
        backgroundColor: color,
      }}
    />
    <Stack gap="2px">
      <Typography
        variant="caption"
        sx={{ fontSize: 10, color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
        {currency(value)}
      </Typography>
      {sinceLabel ? (
        rate !== undefined ? (
          <DataRateChange rate={rate} label={sinceLabel} />
        ) : (
          <Typography
            variant="caption"
            sx={{ fontSize: 9, color: "text.disabled" }}
          >
            {sinceLabel}: —
          </Typography>
        )
      ) : null}
    </Stack>
  </Stack>
);

const GapRow = ({ color, value, delta }) => {
  if (value == null) return null;
  const ahead = value >= 0;
  const accent = ahead ? "success.main" : "error.main";
  return (
    <Stack direction="row" gap="10px" alignItems="center">
      <Box
        sx={{
          width: "4px",
          height: "44px",
          borderRadius: "3px",
          backgroundColor: color,
        }}
      />
      <Stack gap="2px">
        <Typography
          variant="caption"
          sx={{ fontSize: 10, color: "text.secondary" }}
        >
          {ahead ? "Ahead of Goal" : "Behind Goal"}
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: accent }}>
          {ahead ? "+" : "-"}
          {currency(Math.abs(value))}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontSize: 9, color: "text.disabled" }}
        >
          {delta == null
            ? "Cumulative vs goal"
            : `This period: ${delta >= 0 ? "+" : "-"}${currency(Math.abs(delta))}`}
        </Typography>
      </Stack>
    </Stack>
  );
};

const BUCKET_LABEL = {
  day: (key) => dayjs(key).format("ddd, MMM D"),
  week: (key) =>
    `${dayjs(key).format("MMM D")} - ${dayjs(key).add(6, "day").format("MMM D")}`,
  month: (key) => dayjs(`${key}-01`).format("MMMM YYYY"),
};

const SINCE_LABEL = {
  day: "Since Last Day",
  week: "Since Last Week",
  month: "Since Last Month",
};

export const DashboardThroughputTooltip = ({
  axisValue,
  dataIndex,
  series = [],
  groupBy = "day",
}) => {
  const bucketKey = axisValue;
  if (!bucketKey) return null;

  const revenueSeries =
    series.find((s) => s?.dataKey === "revenue") || series[0];
  const goalSeries = series.find((s) => s?.dataKey === "goal");
  const gapSeries = series.find((s) => s?.dataKey === "gap");

  const revenueData = revenueSeries?.data || [];
  const goalData = goalSeries?.data || [];
  const gapData = gapSeries?.data || [];

  const revenue = revenueData[dataIndex] || 0;
  const prevRevenue =
    dataIndex > 0 ? revenueData[dataIndex - 1] || 0 : undefined;
  const revenueRate = getRate(revenue, prevRevenue);

  const goal = goalData[dataIndex] || 0;

  const gap = gapData[dataIndex];
  const prevGap = dataIndex > 0 ? gapData[dataIndex - 1] : null;
  const gapDelta =
    gap != null && prevGap != null ? gap - prevGap : null;

  const labelFmt = BUCKET_LABEL[groupBy] || BUCKET_LABEL.day;
  const since = SINCE_LABEL[groupBy] || SINCE_LABEL.day;

  return (
    <StyledBarTooltip>
      <Stack
        gap="2px"
        pb="8px"
        borderBottom="1px solid"
        borderColor="hubColors.greyLight"
      >
        <Stack direction="row" gap="6px" alignItems="center">
          <HubIcon icon="Calendar" size={11} />
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12 }}>
            {labelFmt(bucketKey)}
          </Typography>
        </Stack>
      </Stack>

      <Stack gap="10px" pt="10px">
        <MetricRow
          color={revenueSeries?.color || "#df9e44"}
          label="Throughput"
          value={revenue}
          rate={revenueRate}
          sinceLabel={since}
        />
        <MetricRow
          color={goalSeries?.color || "#cd683e"}
          label="Goal"
          value={goal}
        />
        <GapRow
          color={gapSeries?.color || "#1F4E79"}
          value={gap}
          delta={gapDelta}
        />
      </Stack>
    </StyledBarTooltip>
  );
};

export default DashboardThroughputTooltip;
